import { Fragment, useState, useRef } from 'react';
import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
    Transition,
} from '@headlessui/react';
import { MagnifyingGlassIcon, TrashIcon, DocumentArrowUpIcon } from '@heroicons/react/20/solid';
import type { ProductInventoryItem } from '../services/productService';
import { searchProductsByStoreFromDb } from '../services/productService';
import { parseOrderPdf } from '../lib/parseOrderPdf';
import { reconcileOrderPdf, type PriceChange, type SkippedLine } from '../services/orderPdfService';

export type OrderLineItem = {
    product_id: string;
    product_name: string;
    sku: string;
    units?: string | null;  // e.g. 'EACH', 'P/KG' — determines if decimal qty is allowed
    quantity: number;
    unit_price: number;   // cents
    line_total?: number;  // cents — PDF-sourced total, overrides quantity * unit_price for display
    product_prices_id?: string;
    source?: 'manual' | 'pdf';
};

type Props = {
    storeId: string;
    items: OrderLineItem[];
    onItemsChange: (items: OrderLineItem[]) => void;
};

const FIELD = 'block w-full rounded-lg border-0 ring-1 ring-inset ring-stone-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-stone-400 focus:ring-2 focus:ring-gray-900 focus:outline-none';

function formatRand(cents: number): string {
    return `R${(cents / 100).toFixed(2)}`;
}

export default function OrderItemsSection({ storeId, items, onItemsChange }: Props) {
    const [productQuery, setProductQuery] = useState('');
    const [searchResults, setSearchResults] = useState<ProductInventoryItem[]>([]);
    const [searching, setSearching] = useState(false);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [pdfBusy, setPdfBusy] = useState(false);
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [pdfSummary, setPdfSummary] = useState<{ matchedCount: number; priceChanges: PriceChange[]; skipped: SkippedLine[] } | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const runProductSearch = (value: string) => {
        setProductQuery(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        if (!storeId || !value.trim()) {
            setSearchResults([]);
            return;
        }
        searchDebounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const results = await searchProductsByStoreFromDb(storeId, value);
                setSearchResults(results);
            } catch {
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);
    };

    const addItem = (product: ProductInventoryItem) => {
        const existingIdx = items.findIndex((i) => i.product_id === product.id);
        if (existingIdx !== -1) {
            const updated = [...items];
            updated[existingIdx] = { ...updated[existingIdx], quantity: updated[existingIdx].quantity + 1 };
            onItemsChange(updated);
        } else {
            onItemsChange([
                {
                    product_id: product.id,
                    product_name: product.name,
                    sku: product.sku,
                    units: product.units ?? null,
                    quantity: 1,
                    unit_price: product.display_price ?? 0,
                    product_prices_id: product.display_price_id ?? undefined,
                    source: 'manual',
                },
                ...items,
            ]);
        }
        setProductQuery('');
        setSearchResults([]);
    };

    const updateQuantity = (idx: number, quantity: number) => {
        if (quantity <= 0) {
            onItemsChange(items.filter((_, i) => i !== idx));
            return;
        }
        onItemsChange(items.map((item, i) => (i === idx ? { ...item, quantity } : item)));
    };

    const removeItem = (idx: number) => {
        onItemsChange(items.filter((_, i) => i !== idx));
    };

    const handlePdfUpload = async (file: File) => {
        if (!storeId) {
            setPdfError('Select a store before uploading an order PDF.');
            return;
        }
        setPdfBusy(true);
        setPdfError(null);
        setPdfSummary(null);
        try {
            const parsedLines = await parseOrderPdf(file);
            console.log('[PDF Import] Raw extracted lines:', parsedLines);
            if (parsedLines.length === 0) {
                setPdfError('No product lines could be read from this PDF. Check the file and try again.');
                return;
            }

            const result = await reconcileOrderPdf(storeId, parsedLines);

            const newItems: OrderLineItem[] = result.matched.map((m) => ({
                product_id: m.product_id,
                product_name: m.name,
                sku: m.sku,
                units: m.units ?? null,
                // Round to integer for non-weight items — PDF quantities like 1.00 become 1
                quantity: m.units === 'P/KG' ? m.quantity : Math.round(m.quantity),
                unit_price: m.unit_price_cents,
                line_total: m.line_total_cents,
                product_prices_id: m.product_prices_id,
                source: 'pdf' as const,
            }));

            // Append rather than replace — keeps anything already added manually.
            // Duplicate codes from the PDF (e.g. multiple banana lines) are kept
            // as separate cart lines, matching how they appear in the source PDF.
            onItemsChange([...items, ...newItems]);
            setPdfSummary({ matchedCount: newItems.length, priceChanges: result.price_changes, skipped: result.skipped });
        } catch (err) {
            setPdfError(err instanceof Error ? err.message : 'Failed to process this PDF.');
        } finally {
            setPdfBusy(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const total = items.reduce((sum, i) => sum + (i.line_total ?? i.quantity * i.unit_price), 0);

    return (
        <div className="border-t border-stone-200 pt-6 space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">Order Items</h4>
                <div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void handlePdfUpload(file);
                        }}
                    />
                    <button
                        type="button"
                        disabled={pdfBusy || !storeId}
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-stone-50 disabled:opacity-50 transition"
                    >
                        <DocumentArrowUpIcon className="h-4 w-4" />
                        {pdfBusy ? 'Processing…' : 'Upload Order PDF'}
                    </button>
                </div>
            </div>

            {!storeId && (
                <p className="text-xs text-stone-400">Select a store above to search products or upload an order PDF.</p>
            )}

            {pdfError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                    {pdfError}
                </div>
            )}

            {pdfSummary && (
                <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5 text-xs space-y-2">
                    <p className="font-medium text-stone-700">
                        Imported {pdfSummary.matchedCount} line(s) from PDF.
                        {items.filter(i => i.source === 'manual').length > 0 && (
                            <span className="ml-2 text-blue-600">
                                {items.filter(i => i.source === 'manual').length} added manually.
                            </span>
                        )}
                    </p>
                    {pdfSummary.priceChanges.length > 0 && (
                        <div>
                            <p className="text-stone-500 font-medium mb-1">Prices updated:</p>
                            <ul className="space-y-0.5">
                                {pdfSummary.priceChanges.map((c) => (
                                    <li key={c.sku} className="text-stone-600">
                                        {c.name} ({c.sku}): {formatRand(c.old_price_cents)} → <span className="font-medium text-[#ee2b74]">{formatRand(c.new_price_cents)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {pdfSummary.skipped.length > 0 && (
                        <div>
                            <p className="text-amber-600 font-medium mb-1">Skipped (no matching product):</p>
                            <ul className="space-y-0.5">
                                {pdfSummary.skipped.map((s, idx) => (
                                    <li key={`${s.code}-${idx}`} className="text-stone-600">
                                        {s.code} — {s.description}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Manual product search */}
            <Combobox value={null} onChange={(product: ProductInventoryItem | null) => product && addItem(product)}>
                <div className="relative">
                    <div className="relative">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                        <ComboboxInput
                            className={`${FIELD} pl-10`}
                            placeholder={storeId ? 'Search products to add…' : 'Select a store first'}
                            disabled={!storeId}
                            value={productQuery}
                            onChange={(e) => runProductSearch(e.target.value)}
                            displayValue={() => productQuery}
                        />
                    </div>
                    <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {searching ? (
                                <div className="px-4 py-2 text-stone-400">Searching…</div>
                            ) : productQuery && searchResults.length === 0 ? (
                                <div className="px-4 py-2 text-stone-400">No products found for "{productQuery}"</div>
                            ) : (
                                searchResults.map((product) => (
                                    <ComboboxOption
                                        key={product.id}
                                        value={product}
                                        className={({ focus }) => `relative cursor-default select-none py-2 pl-4 pr-4 ${focus ? 'bg-stone-50' : ''}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-gray-900">{product.name}</p>
                                                <p className="text-xs text-stone-400">SKU: {product.sku}</p>
                                            </div>
                                            <span className="text-stone-600 font-medium">
                        {product.display_price != null ? formatRand(product.display_price) : '—'}
                      </span>
                                        </div>
                                    </ComboboxOption>
                                ))
                            )}
                        </ComboboxOptions>
                    </Transition>
                </div>
            </Combobox>

            {/* Cart */}
            {items.length > 0 && (
                <div className="rounded-lg border border-stone-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Product</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 w-24">Qty</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 w-24">Price</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 w-24">Subtotal</th>
                            <th className="px-3 py-2 w-10" />
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                        {items.map((item, idx) => (
                            <tr key={`${item.product_id}-${idx}`}>
                                <td className="px-3 py-2">
                                    <p className="font-medium text-gray-900">{item.product_name}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <p className="text-xs text-stone-400">SKU: {item.sku}</p>
                                        {item.source === 'manual' && (
                                            <span className="inline-flex items-center rounded-full bg-blue-50 border border-blue-200 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                                                Added manually
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="number"
                                        min={item.units === 'P/KG' || !Number.isInteger(item.quantity) ? 0.001 : 1}
                                        step={item.units === 'P/KG' || !Number.isInteger(item.quantity) ? '0.001' : '1'}
                                        value={item.quantity}
                                        onChange={(e) => {
                                            const isDecimal = item.units === 'P/KG' || !Number.isInteger(item.quantity);
                                            const val = isDecimal
                                                ? parseFloat(e.target.value) || 0
                                                : parseInt(e.target.value, 10) || 0;
                                            updateQuantity(idx, val);
                                        }}
                                        className="w-20 rounded-md border-0 ring-1 ring-inset ring-stone-300 px-2 py-1 text-sm focus:ring-2 focus:ring-gray-900 focus:outline-none"
                                    />
                                </td>
                                <td className="px-3 py-2 text-stone-600">{formatRand(item.unit_price)}</td>
                                <td className="px-3 py-2 font-medium text-gray-900">{formatRand(item.line_total ?? item.quantity * item.unit_price)}</td>
                                <td className="px-3 py-2 text-right">
                                    <button type="button" onClick={() => removeItem(idx)} className="text-stone-400 hover:text-red-600 transition">
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                        <tfoot className="bg-stone-50 border-t border-stone-200">
                        <tr>
                            <td colSpan={3} className="px-3 py-2 text-right text-sm font-medium text-stone-600">Total</td>
                            <td className="px-3 py-2 font-semibold text-gray-900">{formatRand(total)}</td>
                            <td />
                        </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}